import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Body from "src/components/common/Body";
import Box from "src/components/common/Box";
import Button from "src/components/common/Button";
import cn from "classnames";
import styled from "styled-components";
import DounutChart from "src/components/common/DounutChart";
import { useGetCurrentFulfillQuery } from "../../api/noticeAPI";
import { setFilterFromStateList, setListData } from "src/store/reducers/fulfillListReduce";
import { useDispatch } from "react-redux";

type DashData = {
  store_type: string;
  executeRate: number;
  all: number;
  Y: number;
};

function dashDetail() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const param = useParams().notice_idx;
  const getCurrentFulfill = useGetCurrentFulfillQuery(param);
  const [currentFulFillData, setCurrentFulFillData] = useState<any>();
  const [dashData, setDashData] = useState<DashData[]>();

  useEffect(() => {
    getCurrentFulfill?.data?.data && setCurrentFulFillData(getCurrentFulfill.data.data);
  }, [getCurrentFulfill]);

  useEffect(() => {
    const result: DashData[] = [];
    if (currentFulFillData) {
      const keyList = Object.keys(currentFulFillData.executeRate);
      for (let i = 0; i < keyList.length; i++) {
        const temp: DashData = { store_type: "", Y: 0, all: 0, executeRate: 0 };
        temp.store_type = keyList[i];
        temp.Y = currentFulFillData.Y[keyList[i]];
        temp.all = currentFulFillData.all[keyList[i]];
        temp.executeRate = currentFulFillData.executeRate[keyList[i]];
        result.push(temp);
      }
    }
    setDashData(result);
  }, [currentFulFillData]);

  const getDistribution = (type: string) => {
    // 매장 구분 코드별 이름. 백엔드 StoreType enum 및 운영 common_code 와 같아야 한다.
    // "A": 백화점, "B": 대리점, "C": 직영점, "ADF": 면세점, "O": 상설, "OL": 아울렛
    // VMD-396 이전에는 "O" 를 아울렛으로 적어 상설 매장이 아울렛 항목으로 집계됐다.
    if (type === "A") {
      return "백화점";
    } else if (type === "B") {
      return "대리점";
    } else if (type === "C") {
      return "직영점";
    } else if (type === "ADF") {
      return "면세점";
    } else if (type === "O") {
      return "상설";
    } else if (type === "OL") {
      return "아울렛";
    } else {
      return "기타";
    }
  };

  const linkToList = async (val: any) => {
    const initData = [
      { type: "직영점", checked: false },
      { type: "대리점", checked: false },
      { type: "백화점", checked: false },
      { type: "면세점", checked: false },
      { type: "상설", checked: false },
      { type: "아울렛", checked: false },
    ];

    const setStoreType = await getDistribution(val.store_type);
    const setData = await initData.map((v) => (v.type === getDistribution(val.store_type) ? { ...v, checked: true } : { ...v, checked: false }));

    if (val.all === 0) return;

    if (val.all !== 0) {
      dispatch(
        setListData({
          keywordSearchData: "",
          currentPageNumber: 1,
          fulfillSearchArray: [setStoreType],
        }),
      );
      dispatch(setFilterFromStateList({ key: setStoreType, data: setData }));
      navigate("/fulfill-list/" + param);
    }
  };

  return (
    <>
      <header>
        <div>
          <Button variant="text" className="btn-back" onClick={() => navigate(-1)}>
            이행현황
          </Button>
        </div>
      </header>

      <Body>
        <ChartBox>
          <DounutChart color="#00263e" percent={currentFulFillData ? currentFulFillData.total.rate : 0} size="160px" />
          <div>
            <p>이행률</p>
            <p>{currentFulFillData && currentFulFillData.total.rate + "%"}</p>
            <span>*평가완료만 해당</span>
          </div>
        </ChartBox>

        <Box pd="35px 15px 30px">
          <SumDashBox>
            <div>
              <dt>전체</dt>
              <dd>{currentFulFillData && currentFulFillData.total.total ? currentFulFillData.total.total : 0}</dd>
            </div>
            <div>
              <dt>평가 완료</dt>
              <dd>{currentFulFillData && currentFulFillData.total.c ? currentFulFillData.total.c : 0}</dd>
            </div>
            <div>
              <dt>평가 대기(재)</dt>
              <dd>
                {currentFulFillData && currentFulFillData.total.b ? currentFulFillData.total.b : 0}
                <span>({currentFulFillData && currentFulFillData.total.b_re ? currentFulFillData.total.b_re : 0})</span>
              </dd>
            </div>
            <div>
              <dt>이행 전(재)</dt>
              <dd>
                {currentFulFillData && currentFulFillData.total.n ? currentFulFillData.total.n : 0}
                <span>({currentFulFillData && currentFulFillData.total.n_re ? currentFulFillData.total.n_re : 0})</span>
              </dd>
            </div>
          </SumDashBox>
        </Box>

        <TypeDashBox>
          <p>매장유형별</p>
          <ul>
            {dashData &&
              dashData.map((value, index) => {
                return (
                  <li key={index} className={cn({ active: value.all === 0 ? false : true })} onClick={() => linkToList(value)}>
                    <p>
                      <span>{getDistribution(value.store_type)}</span>
                      {value.executeRate}%
                    </p>
                    {value.Y}/{value.all}
                  </li>
                );
              })}
          </ul>
        </TypeDashBox>
      </Body>
    </>
  );
}

const ChartBox = styled.div`
  ${({ theme }) => theme.flexBox.flexMixin("row", "center", "stretch")}
  margin-top: 45px;
  > div:last-child {
    ${({ theme }) => theme.flexBox.flexMixin("column", "center", "flex-start")}
    margin-left: 32px;
    > p {
      font-family: "Poppins";
      font-size: 36px;
      font-weight: 500;
      line-height: 1.1;
      &:first-child {
        color: ${({ theme }) => theme.color.main};
        font-size: 12px;
        font-weight: 700;
        line-height: 20px;
      }
    }
    > span {
      display: block;
      margin-top: 4px;
      color: ${({ theme }) => theme.color.quaternary};
      font-size: 12px;
      font-weight: 700;
      line-height: 20px;
    }
  }
`;

const SumDashBox = styled.dl`
  ${({ theme }) => theme.flexBox.flexMixin("row", "space-around", "stretch")}
  padding: 16px 0;
  border-top: 1px solid ${({ theme }) => theme.color.linePrimary};
  border-bottom: 1px solid ${({ theme }) => theme.color.linePrimary};
  dt {
    color: ${({ theme }) => theme.color.quaternary};
    font-size: 12px;
    font-weight: 700;
    margin-bottom: 8px;
  }
  dd {
    color: #000;
    font-family: "Poppins";
    font-size: 18px;
    > span {
      color: ${({ theme }) => theme.color.quaternary};
    }
  }
`;

const TypeDashBox = styled.div`
  padding: 34px 15px 30px;
  > p {
    font-weight: 700;
    margin-bottom: 16px;
  }
  > ul {
    ${({ theme }) => theme.flexBox.flexMixin()}
    > li {
      width: calc(20% - 5px);
      text-align: center;
      color: ${({ theme }) => theme.color.quaternary};
      > p {
        width: 100%;
        padding: 16px 8px;
        background-color: ${({ theme }) => theme.color.bgPrimary};
        border-radius: 4px;
        margin-bottom: 8px;
        color: ${({ theme }) => theme.color.quaternary};
        font-size: 20px;
        font-weight: 500;
        line-height: 30px;
        > span {
          display: block;
          margin-bottom: 8px;
          color: ${({ theme }) => theme.color.quaternary};
          font-size: 12px;
          line-height: 14px;
        }
      }
      &.active {
        color: ${({ theme }) => theme.color.secondary};
        > p {
          color: #000;
          > span {
            color: ${({ theme }) => theme.color.tertiary};
          }
        }
      }
    }
  }
`;

export default dashDetail;
